import React, { useMemo } from "react";

import {
  faCube,
  faFingerprint,
  faTimer,
} from "@fortawesome/pro-regular-svg-icons";

import {
  isBaseSchemaType,
  SCHEMA_TYPE_OPTIONS,
  SchemaTypeSelector,
  TypeOption,
} from "src/base-components/SchemaTypeSelector";
import { Option } from "src/base-components/Select";
import { CasePropertiesValue } from "src/clients/case-management";
import { Icon } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { Pill } from "src/design-system/Pill";
import { getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

// Define all possible case property types - this will fail if any type is missing
const ALL_PROPERTY_TYPES = [
  "string",
  "integer",
  "number",
  "datetime",
  "tags",
  "enum",
  "entity_ref",
  "event_ref",
  "url",
  "uuid",
] as const satisfies readonly CasePropertiesValue["type"][];

/**
 * BEGINNING OF TYPE ASSERTION
 * This block is used to ensure that all case property types are included in the ALL_PROPERTY_TYPES array.
 * If any type is missing, the type assertion will fail and the build will fail.
 */
type AllTypesIncluded =
  CasePropertiesValue["type"] extends (typeof ALL_PROPERTY_TYPES)[number]
    ? (typeof ALL_PROPERTY_TYPES)[number] extends CasePropertiesValue["type"]
      ? true
      : false
    : false;
const _typeCheck: AllTypesIncluded = true;
/** END OF TYPE ASSERTION */

// Prefixes for reference types
export const ENTITY_REF_PREFIX = "entity_ref:";
export const EVENT_REF_PREFIX = "event_ref:";

// Base property types that can be selected directly (entity_ref and event_ref are handled dynamically with IDs)
const BASE_PROPERTY_TYPES = ALL_PROPERTY_TYPES.filter(
  (type) => type !== "entity_ref" && type !== "event_ref",
);

// Helper to create options from base types
const getBaseTypeOptions = () => {
  return BASE_PROPERTY_TYPES.map((type) => {
    if (isBaseSchemaType(type)) {
      return SCHEMA_TYPE_OPTIONS.find(
        (option) => option.key === type,
      ) as Option;
    }

    return {
      key: type,
      value: (
        <TypeOption
          icon={type === "uuid" ? faFingerprint : undefined}
          label={type}
        />
      ),
    };
  });
};

export type CasePropertyTypeSelectorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  dataLoc?: string;
};

export const CasePropertyTypeSelector: React.FC<
  CasePropertyTypeSelectorProps
> = (props) => (
  <ErrorBoundary
    fallbackErrorContent={
      <Input
        disabled
        error
        placeholder="Something went wrong, unable to load property types"
      />
    }
  >
    <CasePropertyTypeSelectorInner {...props} />
  </ErrorBoundary>
);

const CasePropertyTypeSelectorInner: React.FC<
  CasePropertyTypeSelectorProps
> = ({ value, onChange, disabled, dataLoc }) => {
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas, isLoading: isEntitySchemasLoading } =
    useEntitySchemas({
      baseUrl: workspace.base_url!,
    });
  const { data: eventSchemas, isLoading: isEventSchemasLoading } =
    useEventSchemas();

  // Check if the current value exists in available options
  const invalidType = useMemo(() => {
    if (isEntitySchemasLoading || isEventSchemasLoading || !value) {
      return null;
    }

    if (value.startsWith(ENTITY_REF_PREFIX)) {
      const entityType = value.replace(ENTITY_REF_PREFIX, "");
      const exists = entitySchemas?.entities.some(
        (schema) => schema._id === entityType,
      );
      return exists ? null : { type: "entity" as const, id: entityType };
    }

    if (value.startsWith(EVENT_REF_PREFIX)) {
      const eventType = value.replace(EVENT_REF_PREFIX, "");
      const exists = eventSchemas?.some(
        (schema) => schema.event_type === eventType,
      );
      return exists ? null : { type: "event" as const, id: eventType };
    }

    return null;
  }, [
    value,
    entitySchemas,
    eventSchemas,
    isEntitySchemasLoading,
    isEventSchemasLoading,
  ]);

  // Create dynamic type options
  const typeOptions = useMemo(() => {
    const baseOptions = getBaseTypeOptions();

    // Add entity type options
    const entityOptions =
      entitySchemas?.entities.map((schema) => ({
        key: `${ENTITY_REF_PREFIX}${schema._id}`,
        value: (
          <TypeOption
            icon={<Icon icon={getSchemaIcon(schema)} size="xs" />}
            label={schema._display_name_singular}
          />
        ),
      })) ?? [];

    // Add event type options
    const eventOptions =
      eventSchemas?.map((schema) => ({
        key: `${EVENT_REF_PREFIX}${schema.event_type}`,
        value: (
          <TypeOption
            icon={<Icon icon={getEventIcon(schema)} size="xs" />}
            label={schema.display_name_singular}
          />
        ),
      })) ?? [];

    // Add invalid type as an error option if it exists
    if (invalidType) {
      const typeLabel = invalidType.type === "entity" ? "Entity" : "Event";
      const fallbackIcon = invalidType.type === "entity" ? faCube : faTimer;
      const errorOption = {
        key: value,
        value: (
          <TypeOption
            icon={<Icon icon={fallbackIcon} size="xs" />}
            label={`${typeLabel}: ${invalidType.id}`}
          />
        ),
      };
      return [...baseOptions, errorOption, ...entityOptions, ...eventOptions];
    }

    return [...baseOptions, ...entityOptions, ...eventOptions];
  }, [entitySchemas, eventSchemas, value, invalidType]);

  const invalidTypeLabel = invalidType?.type === "entity" ? "Entity" : "Event";
  const errorTooltip = invalidType ? (
    <span>
      {invalidTypeLabel} type{" "}
      <Pill size="sm" variant="dark-gray">
        <Pill.Text>{invalidType.id}</Pill.Text>
      </Pill>{" "}
      does not exist in current workspace
    </span>
  ) : undefined;

  return (
    <SchemaTypeSelector
      dataLoc={dataLoc}
      disabled={disabled}
      errorTooltip={errorTooltip}
      errored={!!invalidType}
      isLoading={isEntitySchemasLoading || isEventSchemasLoading}
      options={typeOptions}
      value={value}
      fullWidth
      onChange={onChange}
    />
  );
};
